/**
 * Light, dark or system, from one button.
 *
 * A menu rather than a two-state switch, because "follow the operating system" is a third answer
 * and a switch cannot express it. The icon shows what is on screen now; the menu shows what was
 * chosen, which are different things while the preference is "system".
 *
 * Inside the shell the choice lives in the account menu rather than as its own button — it is a
 * personal preference, and that is where personal things go. The standalone button remains for
 * the sign-in page, which has no account menu yet.
 */
import { useState } from 'react';
import CheckRounded from '@mui/icons-material/CheckRounded';
import DarkModeRounded from '@mui/icons-material/DarkModeRounded';
import LightModeRounded from '@mui/icons-material/LightModeRounded';
import SettingsBrightnessRounded from '@mui/icons-material/SettingsBrightnessRounded';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';

import { useColorMode, type ColorModePreference } from '@/theme/colorMode';

interface Option {
  value: ColorModePreference;
  label: string;
  Icon: typeof LightModeRounded;
}

const SYSTEM: Option = { value: 'system', label: 'System', Icon: SettingsBrightnessRounded };

const OPTIONS: Option[] = [
  { value: 'light', label: 'Light', Icon: LightModeRounded },
  { value: 'dark', label: 'Dark', Icon: DarkModeRounded },
  SYSTEM,
];

/**
 * The three choices as menu items, for any menu that wants to carry them — the toggle's own, or
 * the account menu in the shell. `onChosen` lets the host close itself.
 */
export function ColorModeMenuItems({ onChosen }: { onChosen?: () => void }): JSX.Element {
  const { preference, setPreference } = useColorMode();
  return (
    <>
      {OPTIONS.map((option) => (
        <MenuItem
          key={option.value}
          selected={option.value === preference}
          onClick={() => {
            setPreference(option.value);
            onChosen?.();
          }}
        >
          <ListItemIcon>
            <option.Icon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={option.label} />
          {option.value === preference ? (
            <CheckRounded fontSize="small" sx={{ ml: 4 }} aria-hidden />
          ) : null}
        </MenuItem>
      ))}
    </>
  );
}

export function ColorModeToggle({ onBrand = false }: { onBrand?: boolean }): JSX.Element {
  const { mode, preference } = useColorMode();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const current = OPTIONS.find((option) => option.value === preference) ?? SYSTEM;
  const Icon = mode === 'dark' ? DarkModeRounded : LightModeRounded;

  return (
    <>
      <Tooltip title={`Appearance: ${current.label.toLowerCase()}`}>
        <IconButton
          size="small"
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-label={`Appearance, currently ${current.label.toLowerCase()}`}
          aria-haspopup="menu"
          sx={onBrand ? { color: 'inherit' } : undefined}
        >
          <Icon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <ColorModeMenuItems onChosen={() => setAnchor(null)} />
      </Menu>
    </>
  );
}
